import React from 'react';
import styled from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { FiLock, FiUser, FiCalendar } from 'react-icons/fi';
import InisisAuthButton, { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import { ChangePasswordInfoDto } from '@/services/changePasswordService';

interface IdentityVerificationStepProps {
  userInfo: ChangePasswordInfoDto;
  onSuccess: (result: InisisAuthResult) => void;
}

/**
 * 본인인증 단계 컴포넌트
 * 이니시스 본인인증을 통해 사용자 확인
 */
const IdentityVerificationStep: React.FC<IdentityVerificationStepProps> = ({
  userInfo,
  onSuccess,
}) => {
  /**
   * 만료일 포맷팅
   */
  const formatExpiredAt = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Container>
      <Header>
        <IconWrapper>
          <FiLock size={24} />
        </IconWrapper>
        <Title>본인인증</Title>
        <Description>비밀번호 변경을 위해 본인인증이 필요합니다.</Description>
      </Header>

      <InfoCard>
        <InfoItem>
          <InfoIcon>
            <FiUser size={18} />
          </InfoIcon>
          <InfoContent>
            <InfoLabel>요청자</InfoLabel>
            <InfoValue>{userInfo.userName}</InfoValue>
          </InfoContent>
        </InfoItem>
        
        <Divider />
        
        <InfoItem>
          <InfoIcon>
            <FiCalendar size={18} />
          </InfoIcon>
          <InfoContent>
            <InfoLabel>만료일</InfoLabel>
            <InfoValue>{formatExpiredAt(userInfo.expiredAt)}</InfoValue>
          </InfoContent>
        </InfoItem>
      </InfoCard>

      <NoticeBox>
        <NoticeIcon>💡</NoticeIcon>
        <NoticeText>
          본인인증은 등록된 <strong>이름</strong>과 <strong>휴대폰 번호</strong>로 진행됩니다.
          <br />
          인증 정보가 일치해야 비밀번호를 변경할 수 있습니다.
        </NoticeText>
      </NoticeBox>

      <ButtonWrapper>
        <InisisAuthButton
          $variant="gradient"
          $size="md"
          $title="본인인증 하기"
          onSuccess={onSuccess}
        />
      </ButtonWrapper>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.2) 100%);
  color: #6366f1;
`;

const Title = styled.h2`
  ${typography.heading.h3}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const Description = styled.p`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
`;

const InfoCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: ${semanticColors.background.tertiary};
  border-radius: 12px;
  border: 1px solid ${semanticColors.border.primary};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const InfoIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${semanticColors.background.secondary};
  color: ${semanticColors.text.tertiary};
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InfoLabel = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
`;

const InfoValue = styled.span`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.primary};
`;

const Divider = styled.div`
  height: 1px;
  background: ${semanticColors.border.primary};
  margin: 16px 0;
`;

const NoticeBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.12) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
`;

const NoticeIcon = styled.span`
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
`;

const NoticeText = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.secondary};
  line-height: 1.5;
  margin: 0;
  
  strong {
    color: #6366f1;
    font-weight: 600;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  
  button {
    width: 100%;
  }
`;

export default IdentityVerificationStep;
